import { useMemo } from "react";

import { api, type ApiError } from "./client";
import { useResource } from "./useResource";
import type { ServiceStatus } from "../types";

// Two collections, two endpoints — processes the repository runs inside the
// devbox, and backing stores it runs beside it. Nothing here discriminates
// them by a `kind` field, because nothing needs to: the endpoint a row came
// from is its group (specifications/services.md §Processes and services).

// --- Processes -------------------------------------------------------------

// GET /api/v1/tasks/:id/processes
export type ApiProcessPort = {
  readonly kind: "http" | "tcp";
  readonly port: number;
  // Set only when the port is public AND its tunnel is reserved + active.
  readonly preview_url: string | null;
  // The port a single-link surface (task header, chat) prefers when more than
  // one is public. At most one true across a task's ports.
  readonly primary: boolean;
  readonly public: boolean;
  readonly public_id: string;
};

export type ApiProcess = {
  readonly auto_start: boolean;
  readonly description: string | null;
  readonly last_status_at: string | null;
  readonly name: string;
  readonly ports: ReadonlyArray<ApiProcessPort>;
  readonly process_id: string;
  readonly public_id: string;
  readonly status: string;
};

export type PortView = {
  readonly id: string;
  readonly isPrimary: boolean;
  readonly isPublic: boolean;
  readonly kind: "http" | "tcp";
  readonly port: number;
  readonly previewUrl: string | null;
};

export type ProcessView = {
  readonly autoStart: boolean;
  readonly description: string | null;
  readonly id: string;
  readonly name: string;
  readonly ports: ReadonlyArray<PortView>;
  readonly processId: string;
  readonly status: ServiceStatus;
};

// --- Services (backing stores) ---------------------------------------------

// GET /api/v1/tasks/:id/services
export type ApiService = {
  readonly image: string;
  readonly last_status_at: string | null;
  readonly name: string;
  // The address the app connects to. A backing store has no port of its own:
  // it is reached at its alias on the session network.
  readonly network_alias: string;
  readonly public_id: string;
  // The template this row came from, for a container something inside the
  // sandbox asked for rather than one the repository declared at boot. Null
  // for everything declared (`on-demand-services.md` §Services tab).
  readonly requested_from?: string | null;
  readonly service_id: string;
  readonly status: string;
};

export type BackingStoreView = {
  readonly id: string;
  readonly image: string;
  readonly name: string;
  readonly networkAlias: string;
  /** The template an instance came from; null for a declared backing store. */
  readonly requestedFrom: string | null;
  readonly status: ServiceStatus;
};

const mapStatus = (status: string): ServiceStatus => {
  const s = status.toLowerCase();
  if (s === "up" || s === "running" || s === "healthy") {
    return "up";
  }
  if (s === "starting" || s === "pending") {
    return "starting";
  }
  return "down";
};

export const mapProcess = (process: ApiProcess): ProcessView => ({
  autoStart: process.auto_start,
  description: process.description,
  id: process.public_id,
  name: process.name,
  ports: process.ports.map((port) => ({
    id: port.public_id,
    isPrimary: port.primary,
    isPublic: port.public,
    kind: port.kind,
    port: port.port,
    previewUrl: port.preview_url,
  })),
  processId: process.process_id,
  status: mapStatus(process.status),
});

export const mapService = (service: ApiService): BackingStoreView => ({
  id: service.public_id,
  image: service.image,
  name: service.name,
  networkAlias: service.network_alias,
  requestedFrom: service.requested_from ?? null,
  status: mapStatus(service.status),
});

export type UseTaskProcesses = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly processes: ReadonlyArray<ProcessView>;
  readonly refetch: () => void;
};

export const useTaskProcesses = (taskId: string | null): UseTaskProcesses => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiProcess>
  >(taskId ? `/tasks/${taskId}/processes` : null);
  const processes = useMemo(() => (data ?? []).map(mapProcess), [data]);
  return { error, loading, processes, refetch };
};

export type UseTaskServices = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly services: ReadonlyArray<BackingStoreView>;
};

export const useTaskServices = (taskId: string | null): UseTaskServices => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiService>
  >(taskId ? `/tasks/${taskId}/services` : null);
  const services = useMemo(() => (data ?? []).map(mapService), [data]);
  return { error, loading, refetch, services };
};

// --- Control ---------------------------------------------------------------
//
// Each returns immediately with an optimistic status; the daemon confirms the
// final state via a `process.status.changed` / `service.status.changed` event.

export const startProcess = (id: string): Promise<unknown> =>
  api.post(`/processes/${id}/start`, {});

export const stopProcess = (id: string): Promise<unknown> =>
  api.post(`/processes/${id}/stop`, {});

export const restartProcess = (id: string): Promise<unknown> =>
  api.post(`/processes/${id}/restart`, {});

export const restartAllProcesses = (taskId: string): Promise<unknown> =>
  api.post(`/tasks/${taskId}/processes/restart-all`, {});

// The task header's "Run app" (live-branch-previews.md §5 The affordance):
// every declared process that isn't already up or starting, in one call.
export const startAllProcesses = (taskId: string): Promise<unknown> =>
  api.post(`/tasks/${taskId}/processes/start-all`, {});

export const startService = (id: string): Promise<unknown> =>
  api.post(`/services/${id}/start`, {});

export const stopService = (id: string): Promise<unknown> =>
  api.post(`/services/${id}/stop`, {});

export const restartService = (id: string): Promise<unknown> =>
  api.post(`/services/${id}/restart`, {});

export const restartAllServices = (taskId: string): Promise<unknown> =>
  api.post(`/tasks/${taskId}/services/restart-all`, {});

// The public-preview flag lives on a **port**, not a process: one process may
// serve a public web port and a private debug port.
export const setPortPublic = (
  portId: string,
  isPublic: boolean,
): Promise<unknown> =>
  api.patch(`/process-ports/${portId}`, { public: isPublic });
